import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import { Box } from '@mui/material'
import React from 'react'
import { useTheme } from '@mui/material/styles'

import useTracking from 'app/tracking/useTracking'
import { ANALYTICS_OBJECTS } from 'app/utils/constants'

interface BackButtonProps {
    /** Callback function to handle click events */
    onClick: () => void
}

/**
 * BackButton component
 * Renders a clickable chevron icon for navigation
 * @param {BackButtonProps} props - Component props
 * @param {() => void} props.onClick - Callback function to handle click events
 * @returns {JSX.Element} The rendered BackButton component
 */
function BackButton({ onClick }: BackButtonProps) {
    const { trackClick } = useTracking()
    const theme = useTheme()

    const handleClick = () => {
        trackClick(ANALYTICS_OBJECTS.BACK_BUTTON)
        window.scrollTo(0, 0) // Scroll to top of page to show the header by default
        onClick()
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handleClick()
        }
    }

    return (
        <Box
            component="button"
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="button"
            aria-label="Go back"
            sx={{
                cursor: 'pointer',
                color: theme.color.figGreen,
                background: 'none',
                border: 'none',
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:focus': {
                    outline: `2px solid ${theme.color.figGreen}`,
                    outlineOffset: '2px'
                }
            }}
        >
            <ChevronLeftIcon sx={{ width: '36px', height: '36px' }} />
        </Box>
    )
}

export default BackButton
